import {Clip} from "../../clips";
import {EventEmitterMixin} from "../../mixins";
import {Serializer} from "../../services";
import {Container} from "pixi.js";
import {Composition} from "../../composition";
import {TrackType} from "./track.types";
import type {InsertStrategy} from "./track.interfaces";
import type {InsertMode} from "./track.types";
import {DefaultInsertStrategy} from "./track.strategies";
import {StackInsertStrategy} from "./track.strategies";
import type {TrackLayer} from "./track.types";
import {arraymove} from "../../utils";
import {Timestamp} from "../../models";
import {CompositionEventsEnum} from "../../../librettoEditor/CompositionEvents";
import {EngineInternalEventsEnum} from "../../EngineInternalEvents";

type Events = {
    update: any;
    frame: number | undefined;
    attach: undefined;
    detach: undefined;
    "clip:added": any;
    "clip:removed": any;
}

export class Track<DerivedClip extends Clip> extends EventEmitterMixin<Events, typeof Serializer>(Serializer) {
    private _disabled: boolean = false;

    public view = new Container();

    public clips: DerivedClip[] = [];

    /**
     * When true, this track will not be automatically removed from the composition
     * when its last clip is removed.
     *
     * This is used by the Script Layer track so it can keep a stable layer id even
     * while temporarily empty (e.g. switching/replacing the ScriptClip).
     */
    public preserveWhenEmpty: boolean = false;

    // Pointer to expected track? (not clear to me)
    public pointer: number = 0;

    public composition: Composition;

    public readonly type: TrackType = 'base';

    public strategy: InsertStrategy<InsertMode> = new DefaultInsertStrategy();

    public get disabled(): boolean {
        return this._disabled;
    }

    public set disabled(value: boolean) {
        if (value && this.clipRef && inGraph(this.clipRef)) {
            this.view.removeChild(this.clipRef.view);
            this.clipRef?.exit();
        }

        this._disabled = value;

        this.trigger('update', undefined);
    }

    public connect(composition: Composition): void {
        this.composition = composition;
    }

    public stacked(value = true): this {
        if (value) {
            this.strategy = new StackInsertStrategy();
            // make sure to realign clips
            this.strategy.update(new Clip(), this);
        } else {
            this.strategy = new DefaultInsertStrategy();
        }

        this.trigger('update', undefined);

        return this;
    }

    public layer(layer: TrackLayer): this {
        const tracks = this.composition?.tracks ?? [];
        const fromIndex = tracks.findIndex((n) => n.id == this.id);
        const lastIndex = tracks.length - 1;
        if (fromIndex == -1) return this;

        let toIndex = 0;
        if (layer == 'bottom') toIndex = lastIndex;
        else if (layer == 'top') toIndex = 0;
        else if (layer < 0) toIndex = 0;
        else if (layer > lastIndex) toIndex = lastIndex;
        else toIndex = layer;

        arraymove(tracks, fromIndex, toIndex);
        this.composition?.stage.setChildIndex(this.view, lastIndex - toIndex);

        this.trigger('update', undefined);
        return this;
    }

    public update(time: Timestamp): void | Promise<void> {
        // case track doesn't contain tracks to render
        if (this.disabled || !this.clips.length) return;

        const {millis} = time;

        // the clip has left the stage
        if (inGraph(this.clipRef) && (!inRange(millis, this.clipRef) || this.clipRef?.disabled)) {
            this.clipRef && this.view.removeChild(this.clipRef.view);
            this.clipRef?.exit(); // call after remove
        }

        if (!inRange(millis, this)) return;

        // search for next clip to be rendered
        for (let idx = 0; idx < this.clips.length; idx++) {
            // start from current position and check all others
            const pointer = (this.pointer + idx) % this.clips.length;

            // get last and current clip
            const clip = this.clips[pointer];
            const last = this.clips[pointer - 1];

            // clip should be rendered
            if (inRange(millis, clip) && !clip.disabled) {
                this.pointer = pointer;

                // the clip has entered the stage
                if (!inGraph(clip)) {
                    clip.enter(); // call before add
                    this.view.addChild(clip.view);
                }

                return clip.update(time);
            }

            // clip will be rendered next
            if (millis < clip.start.millis && millis > (last?.stop.millis ?? 0)) {
                this.pointer = pointer;
                return;
            }
        }
    }

    public async add(clip: DerivedClip, index?: number): Promise<DerivedClip> {
        if (this.composition && !this.composition.renderer) {
            await new Promise(this.composition.resolve('init'));
        }

        console.log("Adding clip to track")

        await clip.init();
        await clip.connect(this);

        await this.strategy.add(clip, this, index);

        clip.on('frame', () => {
            this.strategy.update(clip, this);
        });
        // @ts-ignore
        clip.bubble(this);

        this.trigger('attach', undefined);
        this.trigger(CompositionEventsEnum.CLIP_ADDED, {layerId: this.id, clip: clip});
        await this.composition.prepareClipForDisplay(clip);
        this.composition.trigger(EngineInternalEventsEnum.CLIP_ADDED, {layerId: this.id, clip: clip});
        try {
            this.composition.recomputeEmptyState();
        } catch {}

        return clip;
    }

    public remove<L extends DerivedClip>(clip: L): L | undefined {
        const index = this.clips.findIndex((c) => c.id == clip.id);

        if (clip.state == 'ATTACHED') {
            clip.state = 'READY';
        }

        // Best-effort cleanup for clips that attach additional resources to the stage
        // (e.g. ScriptLayerClip moves per-source video containers directly onto the stage).
        try { clip.exit?.(); } catch {}
        try { (clip as any)?.disconnect?.(); } catch {}

        if (clip.view.parent) {
            this.view.removeChild(clip.view);
        }

        if (index != undefined && index >= 0) {
            this.clips.splice(index, 1);
            this.strategy.update(clip, this);
            this.trigger('detach', undefined);
            this.trigger(CompositionEventsEnum.CLIP_REMOVED, {layerId: this.id, clipId: clip.id});
            // Why did I add this? Internal events - given that for this one at least, we check clips length and
            // then delete the layer? Why did I add the internal event - I forget.
            this.composition.trigger(EngineInternalEventsEnum.CLIP_REMOVED, {layerId: this.id, clipId: clip.id});
            clip.off('*');

            if (this.clips.length === 0) {
                if (!this.preserveWhenEmpty) {
                    this.composition.removeTrack(this);
                }
            }

            return clip;
        }
    }

    public get stop(): Timestamp {
        return this.clips.at(-1)?.stop ?? new Timestamp();
    }

    /**
     * Get the last visible frame of the clip
     */
    public get start(): Timestamp {
        return this.clips.at(0)?.start ?? new Timestamp();
    }

    /**
     * apply a function to all clips within the track
     */
    public apply(fn: (value: DerivedClip) => void): void {
        this.clips.forEach(fn);
    }

    /**
     * Remove the track from the composition
     */
    public detach(): this {
        this.composition?.removeTrack(this);

        return this;
    }

    /**
     * Get the clip that the pointer is
     * currently referencing
     */
    private get clipRef(): DerivedClip | undefined {
        return this.clips[this.pointer];
    }

}

/**
 * Check if a clip or track should be rendered
 * @param millis Time in milliseconds
 * @param node A clip or track
 * @returns True if the time is between the start and stop of the clip/track
 */
function inRange(time: number, node?: Track<Clip> | Clip): boolean {
    if (!node) return false;

    return time >= node.start.millis && time <= node.stop.millis;
}

/**
 * Check if the clip is part of the scene graph to render
 * @param clip The clip to verify
 * @returns True if the clip is part of the scene graph
 */
function inGraph(clip?: Clip): boolean {
    if (!clip) return false;

    return !!clip.view.parent;
}
